import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, ArrowLeft, Shuffle } from 'lucide-react';
import api from '../../utils/api';
import { useSocket } from '../../contexts/SocketContext';
import { useToast } from '../../components/Toast';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import TabBar from '../../components/TabBar';
import { useAuth } from '../../contexts/AuthContext';


export default function VerificationCodePage() {
  const navigate = useNavigate();
  const [currentCode, setCurrentCode] = useState('');
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const { subscribe } = useSocket();
  const { isAdmin } = useAuth();

  const fetchCode = useCallback(() => {
    api.get('/admin/verification-code')
      .then(res => {
        setCurrentCode(res.data.code || '');
        setDate(res.data.date || '');
      })
      .catch(() => {});
  }, []);

  useEffect(() => { fetchCode(); }, [fetchCode]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (event === 'verification_code_updated') fetchCode();
    });
  }, [subscribe, fetchCode]);

  // 生成随机6位验证码并立即保存
  const handleRandom = async () => {
    const code = String(Math.floor(Math.random() * 1000000)).padStart(6, '0');
    setLoading(true);
    try {
      await api.post('/admin/verification-code', { code });
      showToast(`新验证码已生成：${code}`, 'success');
      fetchCode();
    } catch (err) {
      showToast(err.response?.data?.error || '生成失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}
        >
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
            <KeyRound size={16} /> 验证码设置
          </span>
        </span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '24px 16px' }}>
        {/* 当前验证码展示 */}
        <div className="card" style={{ textAlign: 'center', padding: '36px 24px' }}>
          <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 12 }}>
            {date ? `上次更新：${date}` : '尚未设置'}
          </div>
          <div style={{
            fontSize: 56,
            fontWeight: 900,
            letterSpacing: '0.18em',
            color: currentCode ? 'var(--c-primary)' : 'var(--c-text-tertiary)',
            fontVariantNumeric: 'tabular-nums',
            marginBottom: 20,
            fontFamily: 'monospace',
          }}>
            {currentCode || '------'}
          </div>

          {/* 随机生成按钮（仅管理员可见：代理商只看当前码） */}
          {isAdmin && (
            <button
              onClick={handleRandom}
              disabled={loading}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, padding: '14px 32px', borderRadius: 16,
                background: loading ? 'var(--c-border)' : 'var(--c-primary)',
                color: loading ? 'var(--c-text-secondary)' : 'white',
                border: 'none', fontSize: 15, fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                width: '100%', transition: 'all 0.15s'
              }}
            >
              <Shuffle size={18} />
              {loading ? '生成中...' : '生成随机验证码'}
            </button>
          )}

          <p style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginTop: 14, lineHeight: 1.7 }}>
            {isAdmin
              ? <>每次点击生成一个新的6位随机码并立即生效<br />不点击则验证码永久保留，无需每日更新</>
              : <>此验证码由系统管理员维护<br />最新验证码会即时显示在下方</>}
          </p>
        </div>

        {/* 说明卡 */}
        <div className="card" style={{ marginTop: 16, background: 'rgba(0,122,255,0.04)', border: '1px solid rgba(0,122,255,0.12)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--c-primary)', marginBottom: 8 }}>使用规则</div>
          <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', lineHeight: 1.9 }}>
            <div>· 未设置固定密码的用户 / 代理商，使用此验证码登录</div>
            <div>· 首次登录后会引导设置固定密码，之后无需验证码</div>
            {isAdmin && <div>· 点击「生成随机验证码」后旧码立即失效，新码即时生效</div>}
          </div>
        </div>
      </div>
      <TabBar mode="admin" />
    </div>
  );
}
